import * as React from 'react';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  BulkAssemblyMethod,
  DEFAULT_GOLDEN_GATE_PARAMETERS,
  DisplayFragmentProductionMethod,
  FragmentProductionMethod,
  GoldenGateParameters,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {AssemblyParametersControlsLayout} from 'molbio/bulk-assembly/setup-modal/controls/AssemblyParametersControlsLayout';
import {BindingRegionMinLength} from 'molbio/bulk-assembly/setup-modal/controls/BindingRegionMinLength';
import {BindingRegionMinMeltingTemp} from 'molbio/bulk-assembly/setup-modal/controls/BindingRegionMinMeltingTemp';
import {FragmentProductionMethodDropdown} from 'molbio/bulk-assembly/setup-modal/controls/FragmentProductionMethodDropdown';
import {PreRecognitionSiteBases} from 'molbio/bulk-assembly/setup-modal/controls/PreRecognitionSiteBases';
import {PreRecognitionSiteLength} from 'molbio/bulk-assembly/setup-modal/controls/PreRecognitionSiteLength';
import {PrimerPairMaxMeltingTempDifference} from 'molbio/bulk-assembly/setup-modal/controls/PrimerPairMaxMeltingTempDifference';
import {ResetToDefaultsButton} from 'molbio/bulk-assembly/setup-modal/controls/ResetToDefaultsButton';
import {ReviewParametersText} from 'molbio/bulk-assembly/setup-modal/controls/ReviewParametersText';
import {RestrictionEnzymeDropdown} from 'molbio/components/RestrictionEnzymeDropdown';
import EnzymeConstants from 'molbio/enzymes/EnzymeConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';

export function areGoldenGateParametersValid(goldenGateParameters: GoldenGateParameters): boolean {
  return (
    goldenGateParameters.preRecognitionSiteLength != null &&
    goldenGateParameters.bindingRegionMinMeltingTemp != null &&
    goldenGateParameters.bindingRegionMinLength != null &&
    goldenGateParameters.primerPairMaxMeltingTempDifference != null
  );
}

export interface GoldenGateParametersControlsProps {
  goldenGateParameters: GoldenGateParameters;
  patchGoldenGateParameters: (newParams: Partial<GoldenGateParameters>) => void;
  fragmentProductionMethodOverride?: DisplayFragmentProductionMethod;
  isReadOnly: boolean;
}

/**
 * A set of inputs to set parameters for a Golden Gate assembly.
 *
 * @param props Component props
 * @param {GoldenGateParameters} props.goldenGateParameters The parameters that can be updated with this set of inputs.
 * @param {number} props.goldenGateParameters.restrictionEnzymeId The database id of the restriction enzyme for this assembly.
 * @param {FragmentProductionMethod} props.goldenGateParameters.fragmentProductionMethod What strategy to use to produce the assembly fragments
 * @param {(number | null)} props.goldenGateParameters.preRecognitionSiteLength The length in base pairs of the pre-recognition site.
 * @param {string} props.goldenGateParameters.preRecognitionSiteBases A string of bases to use as a pre-recognition site.
 * @param {(number | null)} props.goldenGateParameters.bindingRegionMinMeltingTemp The minimum melting temperature, in degrees Celsius, of the primer binding region.
 * @param {(number | null)} props.goldenGateParameters.bindingRegionMinLength The minimum length, in base pairs, of the primer binding region.
 * @param {(number | null)} props.goldenGateParameters.primerPairMaxMeltingTempDifference The maximum difference, in degrees Celsius, between the melting temperatures of all primers used in this assembly.
 * @param {boolean} props.goldenGateParameters.reuseExistingPrimers Whether to look for existing matching primers before generating new primers for this assembly.
 * @param {function} props.patchGoldenGateParameters A function that takes a subset of Golden Gate parameters and updates state.
 * @param {(DisplayFragmentProductionMethod | undefined)} props.fragmentProductionMethodOverride An override for the fragment production method; locks the dropdown as well.
 * @constructor
 */
export function GoldenGateParametersControls(props: GoldenGateParametersControlsProps): JSX.Element {
  return (
    <AssemblyParametersControlsLayout
      controls={[
        <ReviewParametersText />,
        !props.isReadOnly && (
          <ResetToDefaultsButton
            resetOnClick={() => {
              props.patchGoldenGateParameters(DEFAULT_GOLDEN_GATE_PARAMETERS);
            }}
          />
        ),
        <FormGroup>
          <FieldLabel label="Type IIS Restriction Enzyme" htmlFor="restrictionEnzyme" />
          <RestrictionEnzymeDropdown
            allowedRestrictionEnzymeIds={EnzymeConstants.TYPE_IIS_ENZYME_IDS}
            isDisabled={props.isReadOnly}
            restrictionEnzymeId={props.goldenGateParameters.defaultRestrictionEnzymeId}
            shouldAllowNull={false}
            updateRestrictionEnzymeId={(defaultRestrictionEnzymeId) => {
              analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_ENZYME, {
                actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
                cloningMethod: BulkAssemblyMethod.GOLDEN_GATE,
                enzymeId: defaultRestrictionEnzymeId,
              });
              props.patchGoldenGateParameters({defaultRestrictionEnzymeId});
            }}
          />
        </FormGroup>,
        <FragmentProductionMethodDropdown
          fragmentProductionMethod={
            props.fragmentProductionMethodOverride ??
            props.goldenGateParameters.defaultFragmentProductionMethod
          }
          updateFragmentProductionMethod={(fragmentProductionMethod) => {
            analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_FRAGMENT_PRODUCTION_METHOD, {
              actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
              cloningMethod: BulkAssemblyMethod.GOLDEN_GATE,
              fragmentProductionMethod,
            });
            props.patchGoldenGateParameters({defaultFragmentProductionMethod: fragmentProductionMethod});
          }}
          bulkAssemblyMethod={BulkAssemblyMethod.GOLDEN_GATE}
          dropdownMethods={[
            FragmentProductionMethod.EXISTING_CUT_SITES,
            FragmentProductionMethod.PRIMER_PAIR,
          ]}
          disabled={props.fragmentProductionMethodOverride != null || props.isReadOnly}
        />,
        ...((props.fragmentProductionMethodOverride ??
          props.goldenGateParameters.defaultFragmentProductionMethod) !==
        FragmentProductionMethod.EXISTING_CUT_SITES
          ? [
              <PreRecognitionSiteLength
                preRecognitionSiteLength={props.goldenGateParameters.preRecognitionSiteLength}
                updatePreRecognitionSiteLength={(preRecognitionSiteLength) => {
                  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
                    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
                    cloningMethod: BulkAssemblyMethod.GOLDEN_GATE,
                    parameterName: 'preRecognitionSiteLength',
                    parameterValue: preRecognitionSiteLength,
                  });
                  props.patchGoldenGateParameters({
                    preRecognitionSiteLength,
                    preRecognitionSiteBases:
                      props.goldenGateParameters.preRecognitionSiteBases !== '' &&
                      preRecognitionSiteLength !== props.goldenGateParameters.preRecognitionSiteBases.length
                        ? ''
                        : props.goldenGateParameters.preRecognitionSiteBases,
                  });
                }}
                isDisabled={props.isReadOnly}
              />,
              <PreRecognitionSiteBases
                preRecognitionSiteBases={props.goldenGateParameters.preRecognitionSiteBases}
                updatePreRecognitionSiteBases={(preRecognitionSiteBases) => {
                  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
                    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
                    cloningMethod: BulkAssemblyMethod.GOLDEN_GATE,
                    parameterName: 'preRecognitionSiteBases',
                    parameterValue: preRecognitionSiteBases,
                  });
                  props.patchGoldenGateParameters({
                    preRecognitionSiteBases,
                    preRecognitionSiteLength: preRecognitionSiteBases.length,
                  });
                }}
                isDisabled={props.isReadOnly}
              />,
              <BindingRegionMinLength
                bindingRegionMinLength={props.goldenGateParameters.bindingRegionMinLength}
                updateBindingRegionMinLength={(bindingRegionMinLength) => {
                  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
                    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
                    cloningMethod: BulkAssemblyMethod.GOLDEN_GATE,
                    parameterName: 'bindingRegionMinLength',
                    parameterValue: bindingRegionMinLength,
                  });
                  props.patchGoldenGateParameters({bindingRegionMinLength});
                }}
                isDisabled={props.isReadOnly}
              />,
              <BindingRegionMinMeltingTemp
                bindingRegionMinMeltingTemp={props.goldenGateParameters.bindingRegionMinMeltingTemp}
                updateBindingRegionMinMeltingTemp={(bindingRegionMinMeltingTemp) => {
                  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
                    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
                    cloningMethod: BulkAssemblyMethod.GOLDEN_GATE,
                    parameterName: 'bindingRegionMinMeltingTemp',
                    parameterValue: bindingRegionMinMeltingTemp,
                  });
                  props.patchGoldenGateParameters({bindingRegionMinMeltingTemp});
                }}
                isDisabled={props.isReadOnly}
              />,
              <PrimerPairMaxMeltingTempDifference
                primerPairMaxMeltingTempDifference={
                  props.goldenGateParameters.primerPairMaxMeltingTempDifference
                }
                updatePrimerPairMaxMeltingTempDifference={(primerPairMaxMeltingTempDifference) => {
                  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PRIMER_PARAMETERS, {
                    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
                    cloningMethod: BulkAssemblyMethod.GOLDEN_GATE,
                    parameterName: 'primerPairMaxMeltingTempDifference',
                    parameterValue: primerPairMaxMeltingTempDifference,
                  });
                  props.patchGoldenGateParameters({primerPairMaxMeltingTempDifference});
                }}
                isDisabled={props.isReadOnly}
              />,
              <div />, // spacer, forces previous control to occupy only one column
            ]
          : []),
      ]}
    />
  );
}
